import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChildren,
  Directive,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  Output,
  QueryList,
  SimpleChanges,
  TemplateRef,
  ViewChildren
} from '@angular/core';
import { DropdownOption } from 'src/app/core/models/global.model';
import { SubTableConfig, TableConfig } from './table.model';
import { Helpers } from 'src/app/utils/helpers';

type TableRowDataExtras = {
  isExpanded?: boolean;
  isChecked?: boolean;
};

@Directive({ selector: '[subTableHeader]' })
export class SubTableHeaderDirective { }

@Directive({ selector: '[subTableBody]' })
export class SubTableBodyDirective { }

@Component({
  selector: 'app-table',
  templateUrl: './table.component.html',
  styleUrls: ['./table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TableComponent<A extends TableRowDataExtras> implements OnChanges {
  clickablePageNum: Array<number | string> = [];
  @Input() tableConfig: TableConfig;
  @Input() isLoading: boolean;
  @Input() isHeaderActionDisabled: boolean;
  @Input() tableContent: A[];
  @Input() metaInfo: any;
  @Input() currentPage = 1;
  @Input() defaultSortedCol: string;
  @Input() defaultSortedDir: number;
  @Input() disablePagination = false;
  @Input() selectAriaLabelProperty: string;
  displayedTableContent: A[];
  @Output() tableHeaderAction = new EventEmitter<any>();
  @Output() tableRowAction1 = new EventEmitter<any>();
  @Output() tableRowAction2 = new EventEmitter<any>();
  @Output() updateSort = new EventEmitter<any>();
  @Output() updateResultsPage = new EventEmitter<number>();
  @Output() tablePageSize = new EventEmitter<{ pageSize: number }>();
  @Output() onSelectAll = new EventEmitter<boolean>();
  @Output() onSelectRow = new EventEmitter<{ index: number; checkStatus: boolean }>();
  @Output() onSelectRowRadio: EventEmitter<any> = new EventEmitter<any>();

  @ContentChildren(SubTableHeaderDirective, { read: TemplateRef }) headerTemplates: QueryList<any>;
  @ContentChildren(SubTableBodyDirective, { read: TemplateRef }) bodyTemplates: QueryList<any>;

  @ViewChildren('rowRadioSelect') radioButtons: QueryList<ElementRef<HTMLInputElement>>;
  @ViewChildren('dataRow') tableRows: QueryList<ElementRef<HTMLTableRowElement>>;

  cdr = inject(ChangeDetectorRef);

  activeIndex: number; // will be used for the action dropdown
  numOfPages = 0;
  sortDirection = 1;

  pageSize = 10;
  endIndex = 10;
  lastSortedCol: string;
  paginationRowOptions: DropdownOption[] = [
    // todo - could move this to config if it differs across pages
    { label: '10 rows', value: '10' },
    { label: '25 rows', value: '25' },
    { label: '50 rows', value: '50' },
    { label: '100 rows', value: '100' }
  ];

  isSelectAll = false;
  resultSkeletonLoader = [
    {
      "ul": "loading-content table-loading-content",
      "li": [
        {
          "elements": [
            {
              class: "is--skeleton"
            }
          ]
        }
      ]
    }
  ]


  get resultText(): string {
    return this.tableConfig.resultText?.singular || 'result';
  }

  get resultTextPlural(): string {
    return this.tableConfig.resultText?.plural || 'results';
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.defaultSortedCol) {
      this.defaultSortedCol = changes?.defaultSortedCol?.currentValue;
    }
    if (changes?.sortDirection) {
      this.sortDirection = changes?.sortDirection?.currentValue;
    }
    if (changes?.tableConfig) {
      this.pageSize = changes?.tableConfig?.currentValue?.pageSize;
    }
    if (changes?.tableContent) {
      this.lastSortedCol = this.defaultSortedCol;
      // Update current page back to 1 when this is not the first search results
      if (changes?.tableContent?.previousValue !== undefined) {
        this.currentPage = 1;
      }

      if (changes?.tableContent?.currentValue?.length && this.defaultSortedCol) {
        this.sort(this.lastSortedCol, true);
      } else {
        this.resetTable(this.tableContent);
      }
    }

    setTimeout(() => {
      // auto calculate row height if the row has an info message
      this.tableRows?.forEach(i => {
        const row = i.nativeElement;
        const infoMessage = row.querySelector('div.info-message');
        let height = (infoMessage?.getBoundingClientRect().height || 0).toString();
        height = height === '0' ? '0.9rem' : `${height}px`;
        row.querySelectorAll('td').forEach(i => i.style.paddingBottom = `calc(0.2rem + ${height})`);
      });
    }, 50);
  }

  resetTable(tableContent: A[]): void {
    this.numOfPages = Math.ceil(tableContent?.length / this.pageSize);
    this.endIndex = Math.min(this.pageSize * this.currentPage, tableContent?.length);
    this.displayedTableContent = tableContent?.slice(this.pageSize * (this.currentPage - 1), this.endIndex);
  }

  clearTable(): void {
    this.displayedTableContent = null;
  }

  sort(col: string, useDefaultSort = false, dir?: number): void {
    const lastSortedCol = this.lastSortedCol;

    this.lastSortedCol = col;
    if (useDefaultSort) {
      this.sortDirection = this.defaultSortedDir || 1;
    } else if (col === lastSortedCol) {
      if (!isNaN(dir)) {
        this.sortDirection = dir;
      } else {
        this.sortDirection = -this.sortDirection;
      }
    } else {
      this.sortDirection = 1;
    }

    this.updateSort.emit({ col: this.lastSortedCol, dir: this.sortDirection });
    if (this.tableContent?.length) {
      this.tableContent = this.sortTableContent(
        col,
        lastSortedCol,
        this.tableContent,
        useDefaultSort,
        this.sortDirection
      );
      this.resetTable(this.tableContent);
    }
  }

  sortTableContent(
    col: string,
    lastSortedCol: string,
    tableContent: A[],
    useDefaultSort: boolean,
    sortDirection: number
  ): any {
    const tableContentCopy = [...tableContent];
    if (lastSortedCol === col && !useDefaultSort) {
      return tableContentCopy.reverse();
    } else {
      const sorted = tableContentCopy.sort((a: any, b: any) => {
        let aa = a[col];
        let bb = b[col];

        if (col === 'dateOfBirth' || col === 'addedforTable') {
          // for add/edit user 'Added' column, we need to put
          // records with 'multiple' at the top
          aa = aa === 'multiple' ? 1 : new Date(aa).getTime();
          bb = bb === 'multiple' ? 1 : new Date(bb).getTime();
        }
        if (col === 'lastName') {
          aa = `${a[col]} ${a.firstName} ${a.middleName}`
          bb = `${b[col]} ${b.firstName} ${b.middleName}`
        }
        const aIsNumber = !isNaN(aa);
        const bIsNumber = !isNaN(bb);
        let sortValue;
        if (aIsNumber && !bIsNumber) {
          sortValue = -1;
        } else if (!aIsNumber && bIsNumber) {
          sortValue = 1;
        } else if (aIsNumber && bIsNumber) {
          sortValue = aa - bb;
        } else {
          sortValue = aa ? bb ? aa.localeCompare(bb) : -1 : 1;
        }
        return sortValue * sortDirection;
      });

      if (this.tableConfig.id === 'my-oaa' && col === 'lastName') {
        const last = sorted.filter(item => item[col] === '');
        const first = sorted.filter(item => item[col] !== '');

        return [...first, ...last];
      }

      return sorted;
    }
  }

  /**
   * @todo can possibly be deleted - format date should happen in container component
   */
  formatDate(dateAsString: string): string {
    if (dateAsString) {
      const date = new Date(dateAsString);
      return `${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getDate().toString().padStart(2, '0')}/${date
        .getFullYear()
        .toString()}`;
    } else {
      return '';
    }
  }

  getSortIndicator(column: string): string {
    const icon = 'assets/icons/column-sort-indicator';
    if (this.lastSortedCol !== column || !this.tableContent?.length) {
      return `${icon}.svg`;
    } else if (this.sortDirection < 0) {
      return `${icon}-descending.svg`;
    } else {
      return `${icon}-ascending.svg`;
    }
  }

  handlePageChange(newPageNumber: number, newPageSize: number = this.pageSize): void {
    this.currentPage = newPageNumber;
    this.pageSize = newPageSize;
    this.numOfPages = Math.ceil(this.tableContent?.length / this.pageSize) || 0;
    this.currentPage = this.numOfPages < this.currentPage ? 1 : this.currentPage;
    this.endIndex = Math.min(this.pageSize * this.currentPage, this.tableContent?.length) || 0;
    this.displayedTableContent = this.tableContent?.slice(this.pageSize * (this.currentPage - 1), this.endIndex) ?? [];
    this.updateResultsPage.emit(this.currentPage);
    this.tablePageSize.emit({ pageSize: newPageSize });
  }

  handleHeaderActionClick(payload: any): void {
    this.tableHeaderAction.emit(payload);
  }

  handleAction1Click(e: any, payload: any, isDisabled: boolean = false): void {
    e.preventDefault();

    if (isDisabled) {
      return;
    }

    this.tableRowAction1.emit(payload);
  }

  handleAction2Click(e: any, payload: any): void {
    e.preventDefault();
    this.tableRowAction2.emit(payload);
  }

  /**
   * Not slated for Release 1
   */
  handleViewMore(event: MouseEvent, currentIndex: number): void {
    if (this.activeIndex === null || this.activeIndex !== currentIndex) {
      this.activeIndex = currentIndex;
    } else {
      this.activeIndex = null;
    }
    event.stopPropagation();
  }

  toggleRowClicked(index: number, isExpanded: boolean) {
    if (isExpanded) {
      // collapse the row
      this.toggleRow(index, false);
    } else {
      // expand desired row, collapse all others
      this.displayedTableContent.map((item, i) => {
        if (index === i) {
          this.toggleRow(i, true);
        } else {
          this.toggleRow(i, false);
        }
      });
    }
  }

  toggleSubRowClicked(index: number) {
    this.tableConfig.subTables[index].isExpanded = !this.tableConfig.subTables[index].isExpanded;
  }

  private toggleRow(index: number, isExpanded: boolean): void {
    if (this.displayedTableContent[index].isExpanded === undefined) {
      this.displayedTableContent[index] = Object.assign({ isExpanded }, this.displayedTableContent[index]);
    } else {
      this.displayedTableContent[index].isExpanded = isExpanded;
    }
  }

  selectAll(checkStatus: boolean): void {
    this.isSelectAll = checkStatus;
    this.tableContent = this.tableContent.map(i => ({ ...i, isChecked: checkStatus }));

    this.onSelectAll.emit(checkStatus);
  }

  selectRow(i: number, checkStatus: boolean): void {
    this.tableContent = Helpers.copy(this.tableContent);
    this.tableContent[i].isChecked = checkStatus;
    const uncheckedItem = this.tableContent.find(i => !i.isChecked);

    if (!checkStatus || uncheckedItem) {
      this.isSelectAll = false;
    }

    if (!uncheckedItem) {
      this.isSelectAll = true;
    }

    this.onSelectRow.emit({ index: i, checkStatus });
  }

  sliceViewMore(rowData: any, subTable: SubTableConfig): typeof rowData {
    if (subTable.viewMoreCallback) {
      return subTable.viewMoreCallback(rowData, subTable);
    }

    return rowData;
  }

  toggleViewMore(subTable: SubTableConfig): void {
    subTable.shouldViewMore = !subTable.shouldViewMore;
  }

  selectRowRadio(index: number): void {
    this.radioButtons.get(index).nativeElement.click();
  }

  onRadioSelect(rowData: any): void {
    this.onSelectRowRadio.emit(rowData);
  }

  hasSelectedRowRadio(): boolean {
    return this.radioButtons.some(i => i.nativeElement.checked);
  }

  cdrUpdate(): void {
    this.cdr.detectChanges();
  }
}
